/**
 * Backtesting Engine
 * Evaluates tradescript strategies against historical candle data.
 */
import { StrategyScript, ConditionNode, ExpressionNode, parseStrategy } from "./tradescript-parser";
import { CandleData } from "../types/chart.types";

export interface BacktestConfig {
  initialCapital: number;
  positionSizePercent: number; // % of capital per trade (default 100)
  commissionPercent: number;   // % commission per trade (default 0)
  slippagePercent: number;     // % slippage (default 0)
}

export interface Trade {
  type: "buy" | "sell";
  entryBar: number;
  entryTime: number;
  entryPrice: number;
  exitBar?: number;
  exitTime?: number;
  exitPrice?: number;
  pnl?: number;
  pnlPercent?: number;
  holdingBars?: number;
}

export interface BacktestResult {
  trades: Trade[];
  totalReturn: number;
  totalReturnPercent: number;
  winRate: number;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  avgWin: number;
  avgLoss: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  sharpeRatio: number;
  profitFactor: number;
  maxConsecutiveWins: number;
  maxConsecutiveLosses: number;
  equityCurve: { time: number; equity: number }[];
  initialCapital: number;
  finalCapital: number;
}

const DEFAULT_CONFIG: BacktestConfig = {
  initialCapital: 100000,
  positionSizePercent: 100,
  commissionPercent: 0.03,
  slippagePercent: 0.01,
};

/**
 * Run a full backtest
 */
export function runBacktest(
  code: string,
  candles: CandleData[],
  config: Partial<BacktestConfig> = {},
): BacktestResult {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const strategy = parseStrategy(code);

  if (!strategy.buyCondition || !strategy.sellCondition) {
    return emptyResult(cfg.initialCapital);
  }

  if (candles.length < 50) {
    return emptyResult(cfg.initialCapital);
  }

  // Pre-compute indicator caches
  const cache = new IndicatorCache(candles);

  const trades: Trade[] = [];
  let position: Trade | null = null;
  let capital = cfg.initialCapital;
  const equityCurve: { time: number; equity: number }[] = [];

  // Start from bar 50 to ensure enough data for indicator lookback
  for (let i = 50; i < candles.length; i++) {
    const bar = candles[i];

    if (!position) {
      // Check buy condition
      const shouldBuy = evaluateCondition(strategy.buyCondition, candles, i, cache, strategy.variables);
      if (shouldBuy) {
        const slippage = bar.close * (cfg.slippagePercent / 100);
        const entryPrice = bar.close + slippage;
        position = {
          type: "buy",
          entryBar: i,
          entryTime: bar.time,
          entryPrice,
        };
      }
    } else {
      // Check sell condition
      const shouldSell = evaluateCondition(strategy.sellCondition, candles, i, cache, strategy.variables);
      if (shouldSell) {
        const slippage = bar.close * (cfg.slippagePercent / 100);
        const exitPrice = bar.close - slippage;
        const qty = Math.floor((capital * cfg.positionSizePercent / 100) / position.entryPrice);
        const grossPnl = (exitPrice - position.entryPrice) * qty;
        const commission = (position.entryPrice * qty + exitPrice * qty) * (cfg.commissionPercent / 100);
        const netPnl = grossPnl - commission;

        position.exitBar = i;
        position.exitTime = bar.time;
        position.exitPrice = exitPrice;
        position.pnl = netPnl;
        position.pnlPercent = (netPnl / (position.entryPrice * qty)) * 100;
        position.holdingBars = i - position.entryBar;

        trades.push(position);
        capital += netPnl;
        position = null;
      }
    }

    // Track equity — if in position, use mark-to-market
    const marketValue = position
      ? capital + (bar.close - position.entryPrice) * Math.floor((capital * cfg.positionSizePercent / 100) / position.entryPrice)
      : capital;

    equityCurve.push({ time: bar.time, equity: marketValue });
  }

  // Close any open position at the last bar
  if (position && candles.length > 0) {
    const lastBar = candles[candles.length - 1];
    const exitPrice = lastBar.close;
    const qty = Math.floor((cfg.initialCapital * cfg.positionSizePercent / 100) / position.entryPrice);
    const grossPnl = (exitPrice - position.entryPrice) * qty;
    const commission = (position.entryPrice * qty + exitPrice * qty) * (cfg.commissionPercent / 100);
    const netPnl = grossPnl - commission;

    position.exitBar = candles.length - 1;
    position.exitTime = lastBar.time;
    position.exitPrice = exitPrice;
    position.pnl = netPnl;
    position.pnlPercent = qty > 0 ? (netPnl / (position.entryPrice * qty)) * 100 : 0;
    position.holdingBars = candles.length - 1 - position.entryBar;
    trades.push(position);
    capital += netPnl;
  }

  return computeMetrics(trades, equityCurve, cfg.initialCapital, capital);
}

function emptyResult(initialCapital: number): BacktestResult {
  return {
    trades: [],
    totalReturn: 0,
    totalReturnPercent: 0,
    winRate: 0,
    totalTrades: 0,
    winningTrades: 0,
    losingTrades: 0,
    avgWin: 0,
    avgLoss: 0,
    maxDrawdown: 0,
    maxDrawdownPercent: 0,
    sharpeRatio: 0,
    profitFactor: 0,
    maxConsecutiveWins: 0,
    maxConsecutiveLosses: 0,
    equityCurve: [],
    initialCapital,
    finalCapital: initialCapital,
  };
}

function computeMetrics(
  trades: Trade[],
  equityCurve: { time: number; equity: number }[],
  initialCapital: number,
  finalCapital: number,
): BacktestResult {
  const totalReturn = finalCapital - initialCapital;
  const totalReturnPercent = (totalReturn / initialCapital) * 100;

  const winningTrades = trades.filter(t => (t.pnl || 0) > 0);
  const losingTrades = trades.filter(t => (t.pnl || 0) <= 0);
  const winRate = trades.length > 0 ? (winningTrades.length / trades.length) * 100 : 0;

  const avgWin = winningTrades.length > 0
    ? winningTrades.reduce((s, t) => s + (t.pnl || 0), 0) / winningTrades.length : 0;
  const avgLoss = losingTrades.length > 0
    ? Math.abs(losingTrades.reduce((s, t) => s + (t.pnl || 0), 0) / losingTrades.length) : 0;

  // Max drawdown
  let peak = initialCapital;
  let maxDD = 0;
  let maxDDPercent = 0;
  for (const point of equityCurve) {
    if (point.equity > peak) peak = point.equity;
    const dd = peak - point.equity;
    if (dd > maxDD) {
      maxDD = dd;
      maxDDPercent = (dd / peak) * 100;
    }
  }

  // Sharpe Ratio (annualized, assuming 252 trading days)
  const returns = equityCurve.map((p, i) =>
    i === 0 ? 0 : (p.equity - equityCurve[i - 1].equity) / equityCurve[i - 1].equity
  ).slice(1);
  const avgReturn = returns.length > 0 ? returns.reduce((s, r) => s + r, 0) / returns.length : 0;
  const stdReturn = returns.length > 1
    ? Math.sqrt(returns.reduce((s, r) => s + (r - avgReturn) ** 2, 0) / (returns.length - 1))
    : 0;
  const sharpeRatio = stdReturn > 0 ? (avgReturn / stdReturn) * Math.sqrt(252) : 0;

  // Profit Factor
  const totalWins = winningTrades.reduce((s, t) => s + (t.pnl || 0), 0);
  const totalLosses = Math.abs(losingTrades.reduce((s, t) => s + (t.pnl || 0), 0));
  const profitFactor = totalLosses > 0 ? totalWins / totalLosses : totalWins > 0 ? Infinity : 0;

  // Consecutive wins/losses
  let maxConsWins = 0, maxConsLosses = 0, consWins = 0, consLosses = 0;
  for (const t of trades) {
    if ((t.pnl || 0) > 0) {
      consWins++;
      consLosses = 0;
      maxConsWins = Math.max(maxConsWins, consWins);
    } else {
      consLosses++;
      consWins = 0;
      maxConsLosses = Math.max(maxConsLosses, consLosses);
    }
  }

  return {
    trades,
    totalReturn,
    totalReturnPercent,
    winRate,
    totalTrades: trades.length,
    winningTrades: winningTrades.length,
    losingTrades: losingTrades.length,
    avgWin,
    avgLoss,
    maxDrawdown: maxDD,
    maxDrawdownPercent: maxDDPercent,
    sharpeRatio,
    profitFactor,
    maxConsecutiveWins: maxConsWins,
    maxConsecutiveLosses: maxConsLosses,
    equityCurve,
    initialCapital,
    finalCapital,
  };
}

// ─── Condition Evaluator ────────────────────────────────────────

function evaluateCondition(
  node: ConditionNode,
  candles: CandleData[],
  barIndex: number,
  cache: IndicatorCache,
  vars: Map<string, ExpressionNode>,
): boolean {
  switch (node.type) {
    case "comparison": {
      const left = evaluateExpression(node.left, candles, barIndex, cache, vars);
      const right = evaluateExpression(node.right, candles, barIndex, cache, vars);
      if (left === null || right === null) return false;
      switch (node.op) {
        case ">": return left > right;
        case "<": return left < right;
        case ">=": return left >= right;
        case "<=": return left <= right;
        case "==": return Math.abs(left - right) < 0.0001;
        case "!=": return Math.abs(left - right) >= 0.0001;
      }
      return false;
    }
    case "crosses": {
      const currLeft = evaluateExpression(node.left, candles, barIndex, cache, vars);
      const currRight = evaluateExpression(node.right, candles, barIndex, cache, vars);
      const prevLeft = barIndex > 0 ? evaluateExpression(node.left, candles, barIndex - 1, cache, vars) : null;
      const prevRight = barIndex > 0 ? evaluateExpression(node.right, candles, barIndex - 1, cache, vars) : null;
      if (currLeft === null || currRight === null || prevLeft === null || prevRight === null) return false;
      if (node.direction === "above") {
        return prevLeft <= prevRight && currLeft > currRight;
      } else {
        return prevLeft >= prevRight && currLeft < currRight;
      }
    }
    case "and":
      return evaluateCondition(node.left, candles, barIndex, cache, vars)
        && evaluateCondition(node.right, candles, barIndex, cache, vars);
    case "or":
      return evaluateCondition(node.left, candles, barIndex, cache, vars)
        || evaluateCondition(node.right, candles, barIndex, cache, vars);
  }
}

function evaluateExpression(
  node: ExpressionNode,
  candles: CandleData[],
  barIndex: number,
  cache: IndicatorCache,
  vars: Map<string, ExpressionNode>,
): number | null {
  const bar = candles[barIndex];
  if (!bar) return null;

  switch (node.type) {
    case "number":
      return node.value;

    case "field":
      switch (node.name) {
        case "open": return bar.open;
        case "high": return bar.high;
        case "low": return bar.low;
        case "close": return bar.close;
        case "volume": return bar.volume ?? 0;
      }
      return null;

    case "function":
      return cache.getIndicatorValue(node.name, node.args, barIndex);

    case "variable": {
      const expr = vars.get(node.name);
      if (!expr) return null;
      return evaluateExpression(expr, candles, barIndex, cache, vars);
    }

    case "arithmetic": {
      const l = evaluateExpression(node.left, candles, barIndex, cache, vars);
      const r = evaluateExpression(node.right, candles, barIndex, cache, vars);
      if (l === null || r === null) return null;
      switch (node.op) {
        case "+": return l + r;
        case "-": return l - r;
        case "*": return l * r;
        case "/": return r !== 0 ? l / r : null;
      }
      return null;
    }

    case "prev":
      if (barIndex - node.barsBack < 0) return null;
      return evaluateExpression(node.expr, candles, barIndex - node.barsBack, cache, vars);
  }
}

// ─── Indicator Cache ─────────────────────────────────────────────

class IndicatorCache {
  private cache: Map<string, number[]> = new Map();
  private candles: CandleData[];

  constructor(candles: CandleData[]) {
    this.candles = candles;
  }

  getIndicatorValue(name: string, args: number[], barIndex: number): number | null {
    const key = `${name}_${args.join("_")}`;
    if (!this.cache.has(key)) {
      this.cache.set(key, this.computeIndicator(name, args));
    }
    const values = this.cache.get(key)!;
    return barIndex < values.length ? values[barIndex] : null;
  }

  private computeIndicator(name: string, args: number[]): number[] {
    const closes = this.candles.map(c => c.close);
    const highs = this.candles.map(c => c.high);
    const lows = this.candles.map(c => c.low);
    const volumes = this.candles.map(c => c.volume ?? 0);

    switch (name) {
      case "SMA":
        return this.computeSMA(closes, args[0] || 14);
      case "EMA":
        return this.computeEMA(closes, args[0] || 14);
      case "RSI":
        return this.computeRSI(closes, args[0] || 14);
      case "MACD":
        return this.computeMACD(closes, args[0] || 12, args[1] || 26, args[2] || 9).macd;
      case "MACD_SIGNAL":
        return this.computeMACD(closes, args[0] || 12, args[1] || 26, args[2] || 9).signal;
      case "MACD_HIST":
        return this.computeMACD(closes, args[0] || 12, args[1] || 26, args[2] || 9).histogram;
      case "BOLLINGER_UPPER":
        return this.computeBollinger(closes, args[0] || 20, args[1] || 2).upper;
      case "BOLLINGER_LOWER":
        return this.computeBollinger(closes, args[0] || 20, args[1] || 2).lower;
      case "ATR":
        return this.computeATR(highs, lows, closes, args[0] || 14);
      default:
        return new Array(this.candles.length).fill(0);
    }
  }

  private computeSMA(data: number[], period: number): number[] {
    const result: number[] = [];
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) { result.push(0); continue; }
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) sum += data[j];
      result.push(sum / period);
    }
    return result;
  }

  private computeEMA(data: number[], period: number): number[] {
    const k = 2 / (period + 1);
    const result: number[] = [];
    let ema = 0;
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        result.push(0);
        continue;
      }
      if (i === period - 1) {
        let sum = 0;
        for (let j = 0; j < period; j++) sum += data[j];
        ema = sum / period;
      } else {
        ema = data[i] * k + ema * (1 - k);
      }
      result.push(ema);
    }
    return result;
  }

  private computeRSI(data: number[], period: number): number[] {
    const result: number[] = new Array(data.length).fill(50);
    if (data.length < period + 1) return result;

    let avgGain = 0, avgLoss = 0;
    for (let i = 1; i <= period; i++) {
      const change = data[i] - data[i - 1];
      if (change > 0) avgGain += change;
      else avgLoss += Math.abs(change);
    }
    avgGain /= period;
    avgLoss /= period;

    result[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

    for (let i = period + 1; i < data.length; i++) {
      const change = data[i] - data[i - 1];
      avgGain = (avgGain * (period - 1) + (change > 0 ? change : 0)) / period;
      avgLoss = (avgLoss * (period - 1) + (change < 0 ? Math.abs(change) : 0)) / period;
      result[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
    }
    return result;
  }

  private computeMACD(data: number[], fast: number, slow: number, signal: number) {
    const emaFast = this.computeEMA(data, fast);
    const emaSlow = this.computeEMA(data, slow);
    const macdLine: number[] = data.map((_, i) => emaFast[i] - emaSlow[i]);
    const signalLine = this.computeEMA(macdLine.map((v, i) => i < slow - 1 ? 0 : v), signal);
    const histogram = macdLine.map((v, i) => v - signalLine[i]);
    return { macd: macdLine, signal: signalLine, histogram };
  }

  private computeBollinger(data: number[], period: number, stddev: number) {
    const sma = this.computeSMA(data, period);
    const upper: number[] = [];
    const lower: number[] = [];

    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        upper.push(0);
        lower.push(0);
        continue;
      }
      let sumSqDiff = 0;
      for (let j = i - period + 1; j <= i; j++) {
        sumSqDiff += (data[j] - sma[i]) ** 2;
      }
      const std = Math.sqrt(sumSqDiff / period);
      upper.push(sma[i] + stddev * std);
      lower.push(sma[i] - stddev * std);
    }
    return { upper, lower };
  }

  private computeATR(highs: number[], lows: number[], closes: number[], period: number): number[] {
    const result: number[] = new Array(highs.length).fill(0);
    const tr: number[] = [];

    for (let i = 0; i < highs.length; i++) {
      if (i === 0) {
        tr.push(highs[i] - lows[i]);
      } else {
        tr.push(Math.max(
          highs[i] - lows[i],
          Math.abs(highs[i] - closes[i - 1]),
          Math.abs(lows[i] - closes[i - 1])
        ));
      }
    }

    // SMA of TR for first ATR
    if (tr.length >= period) {
      let sum = 0;
      for (let i = 0; i < period; i++) sum += tr[i];
      result[period - 1] = sum / period;

      for (let i = period; i < tr.length; i++) {
        result[i] = (result[i - 1] * (period - 1) + tr[i]) / period;
      }
    }
    return result;
  }
}
